import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getEGiftCardList } from "../api/eGiftCard.api";
import type {
  EGiftCardFilter,
  EGiftCardListItemResponse,
  EGiftCardPaging,
} from "../types/eGiftCard.types";

const DEFAULT_PAGINATION: EGiftCardPaging = { count: 0, index: 1, size: 20, total: 0 };

const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === "object" && error !== null) {
    const candidate = error as {
      message?: string;
      response?: { data?: { user_message?: string; message?: string } };
    };
    return (
      candidate.response?.data?.user_message ||
      candidate.response?.data?.message ||
      candidate.message ||
      fallback
    );
  }
  return fallback;
};

export const useEGiftCardList = () => {
  const [eGiftCards, setEGiftCards] = useState<EGiftCardListItemResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilterState] = useState<EGiftCardFilter>({});
  const [pagination, setPagination] = useState<EGiftCardPaging>(DEFAULT_PAGINATION);
  const { run: runLatestRequest, loading } = useLatestRequest();

  const setFilter: React.Dispatch<React.SetStateAction<EGiftCardFilter>> = useCallback(
    (nextFilter) => {
      setFilterState((current) =>
        typeof nextFilter === "function" ? nextFilter(current) : nextFilter,
      );
      setPagination((current) => ({ ...current, index: 1 }));
    },
    [],
  );

  const fetchEGiftCards = useCallback(
    async (requestFilter = filter, page = pagination.index) => {
      try {
        setError(null);
        const response = await runLatestRequest((signal) =>
          getEGiftCardList(
            {
              merchant_id: requestFilter.merchant_id,
              point_from: requestFilter.points_required?.min,
              point_to: requestFilter.points_required?.max,
              quantity_from: requestFilter.quantity?.min,
              quantity_to: requestFilter.quantity?.max,
              start_date: requestFilter.start_date,
              end_date: requestFilter.end_date,
              exchange_from: requestFilter.used_quantity?.min,
              exchange_to: requestFilter.used_quantity?.max,
              remaining_from: requestFilter.available_quantity?.min,
              remaining_to: requestFilter.available_quantity?.max,
              status: requestFilter.requested_status,
              index: page - 1,
              size: pagination.size,
            },
            signal,
          ),
        );
        if (!response) return;
        setEGiftCards([...response.list]);
        setPagination((current) => ({
          ...current,
          count: response.paging?.count ?? 0,
          total: response.paging?.total ?? 0,
        }));
      } catch (fetchError) {
        setEGiftCards([]);
        setPagination((current) => ({ ...current, count: 0, total: 0 }));
        setError(getErrorMessage(fetchError, "Failed to load e-gift cards"));
      }
    },
    [filter, pagination.index, pagination.size, runLatestRequest],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => void fetchEGiftCards(), 400);
    return () => window.clearTimeout(timer);
  }, [fetchEGiftCards]);

  return {
    eGiftCards,
    loading,
    error,
    filter,
    setFilter,
    pagination,
    setPagination,
    fetchEGiftCards,
  };
};
